import { Component, OnInit, OnDestroy, HostListener, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AppConfig } from '../../../../../app.config';
import { HelpersService } from '../../../../../services/helpers.service';
import { ServiceRequestService } from '../../../../../services/servicerequest.service';
import { JobcardService } from '../../../../../services/jobcard.service';
import { ReceiptTemplateService } from '../services/receipt-template.service';
import { ReceiptTemplatePrintService } from '../services/receipt-template-print.service';
import {
  ReceiptTemplate, TemplateElement, TemplateType, PageSizePreset,
  PAGE_SIZE_PRESETS, generateId, createDefaultElement,
} from '../models/receipt-template.model';
import { MergeTag, mapJobCardToSampleData, getSampleData, CanvasDataMap } from '../models/merge-tag.model';
import { TagDropEvent, TemplateCanvasComponent } from './template-canvas/template-canvas.component';
import { ChecklistService } from '../../../../../services/checklist.service';

@Component({
  selector: 'app-template-editor',
  templateUrl: './template-editor.component.html',
  styleUrls: ['./template-editor.component.scss'],
  providers: [MessageService],
})
export class TemplateEditorComponent implements OnInit, OnDestroy {

  /** Reference to the canvas component — used to call forceRemeasure() synchronously before print capture. */
  @ViewChild(TemplateCanvasComponent) private canvasRef!: TemplateCanvasComponent;

  public template: ReceiptTemplate = this.createBlankTemplate();
  /** All currently selected elements.  Empty = nothing selected; length 1 = shows properties panel. */
  public selectedElements: TemplateElement[] = [];
  public previewMode = false;
  public saving = false;
  public zoom = 100;
  public pageSizePresets = PAGE_SIZE_PRESETS;
  public selectedPreset: PageSizePreset | null = PAGE_SIZE_PRESETS[0];
  public showGrid = true;

  // Undo/Redo history
  private history: TemplateElement[][] = [];
  private historyIndex = -1;
  private isUndoRedo = false;

  public templateTypeOptions: { label: string; value: TemplateType }[] = [];

  // ── Test-print job card data ───────────────────────────────────────────────
  /** Job card ID typed by the user into the test-print input. */
  public testJobCardId      = '';
  /** Resolved real data mapped from the API response; drives canvas preview. */
  public liveJobData: CanvasDataMap | null = null;
  public isLoadingTestData  = false;

  private destroy$ = new Subject<void>();
  private templateId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private appConfig: AppConfig,
    private helpersService: HelpersService,
    private templateService: ReceiptTemplateService,
    private printService: ReceiptTemplatePrintService,
    private messageService: MessageService,
    private serviceRequestService: ServiceRequestService,
    private jobcardService: JobcardService,
    private checklistService: ChecklistService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.buildTypeOptions();
    this.templateId = this.route.snapshot.paramMap.get('id');
    if (this.templateId) {
      this.loadTemplate(this.templateId);
    } else {
      this.pushHistory();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Keyboard shortcuts ───────────────────────────────────────────────────
  @HostListener('window:keydown', ['$event'])
  onKeyDown(e: KeyboardEvent): void {
    const ctrl = e.ctrlKey || e.metaKey;
    if (ctrl && e.key === 'z' && !e.shiftKey) { e.preventDefault(); this.undo(); }
    if (ctrl && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); this.redo(); }
    if (ctrl && e.key === 's') { e.preventDefault(); this.save(); }
    if ((e.key === 'Delete' || e.key === 'Backspace') && this.selectedElements.length > 0 && !(e.target as HTMLElement).closest('input, textarea')) {
      this.removeSelectedElements();
    }
  }

  // ─── Computed ─────────────────────────────────────────────────────────────
  get isThirdPartyModuleEnabled(): boolean {
    const products = this.helpersService.getPartnerData()?.SubscribedProducts ?? [];
    return products.some((p: any) =>
      ['THIRDPARTY', 'THIRD_PARTY', 'DISPATCH'].includes(p.ProductType) &&
      [1, 3, 4, 20, 50, 51].includes(p.StatusID),
    );
  }

  get canUndo(): boolean { return this.historyIndex > 0; }
  get canRedo(): boolean { return this.historyIndex < this.history.length - 1; }
  get placedTagKeys(): string[] { return this.template.elements.map(e => e.tagKey); }

  /**
   * Returns the data to pass to the canvas:
   *  - real job card data when a test job card is loaded
   *  - sample data with real partner branding when in preview mode with no live data
   *  - null otherwise (design mode — merge tag keys shown as placeholders)
   */
  get effectiveCustomData(): CanvasDataMap | null {
    if (this.liveJobData) return this.liveJobData;
    if (this.previewMode) return getSampleData(this.helpersService.getPartnerData());
    return null;
  }

  // ─── Init helpers ─────────────────────────────────────────────────────────
  private buildTypeOptions(): void {
    this.templateTypeOptions = [{ label: this.translate.instant("bikeshop.modal.update_job_card.tab.jobcard") , value: 'JOB_CARD' }];
    if (this.isThirdPartyModuleEnabled) {
      this.templateTypeOptions.push({ label: this.translate.instant('bikeshop.receipt_template.editor.type_dispatch'), value: 'THIRD_PARTY_DISPATCH' });
    }
  }

  private createBlankTemplate(): ReceiptTemplate {
    return {
      id: '',
      name: this.translate.instant('bikeshop.receipt_template.editor.default_template_name'),
      type: 'JOB_CARD',
      isGlobalDefault: false,
      isActive: true,
      widthMm: 210,
      heightMm: 297,
      orientation: 'portrait',
      elements: [],
      createdAt: '',
      updatedAt: '',
      requiredByDate: '',
    };
  } 

  private loadTemplate(id: string): void {
    this.templateService.getTemplateById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(template => { 
        if (template) {
          this.template = JSON.parse(JSON.stringify(template));
          this.syncPresetFromDimensions();
          this.pushHistory();
        }
      });
  }

  private syncPresetFromDimensions(): void {
    const found = PAGE_SIZE_PRESETS.find(
      p => p.widthMm === this.template.widthMm && p.heightMm === this.template.heightMm,
    );
    this.selectedPreset = found || PAGE_SIZE_PRESETS[PAGE_SIZE_PRESETS.length - 1];
  }

  // ─── Page size ────────────────────────────────────────────────────────────
  onPresetChange(preset: PageSizePreset): void {
    this.pushHistory();
    this.selectedPreset = preset;
    if (preset.widthMm !== null && preset.heightMm !== null) {
      this.template.widthMm = preset.widthMm;
      this.template.heightMm = preset.heightMm;
    }
  }

  onOrientationChange(orientation: 'portrait' | 'landscape'): void {
    if (this.template.orientation !== orientation) {
      const w = this.template.widthMm;
      this.template.widthMm = this.template.heightMm;
      this.template.heightMm = w;
      this.template.orientation = orientation;
      this.syncPresetFromDimensions();
    }
  }

  // ─── Element management ───────────────────────────────────────────────────

  /**
   * Clamps an element's width and height so it fits inside the canvas with a
   * small margin on each side.  Call this after createDefaultElement() and
   * before computing the placement position so the position math never produces
   * negative coordinates on narrow canvases (e.g. 58 mm till-slip layouts).
   */
  private fitToCanvas(el: TemplateElement): void {
    const margin = 5; // mm padding from each edge
    el.width  = Math.min(el.width,  Math.max(10, this.template.widthMm  - margin * 2));
    el.height = Math.min(el.height, Math.max(4,  this.template.heightMm - margin * 2));
  }

  /** Inserts a new free-text element onto the canvas. */
  addFreeText(): void {
    const el = createDefaultElement('__freetext__', 'Free Text', 'text');
    this.fitToCanvas(el);
    const offset = this.template.elements.length * 3;
    el.x = Math.min(10 + offset, this.template.widthMm  - el.width  - 5);
    el.y = Math.min(10 + offset, this.template.heightMm - el.height - 5);
    el.zIndex = this.template.elements.length + 1;
    this.template.elements = [...this.template.elements, el];
    this.selectedElements = [el];
    this.pushHistory();
  }

  /** Inserts a horizontal line (divider) onto the canvas, spanning the full canvas width minus margins. */
  addDivider(): void {
    const el = createDefaultElement('__divider__', 'Line', 'divider');
    // Span the full usable canvas width (canvas minus 10mm margins each side).
    el.width  = Math.max(10, this.template.widthMm - 20);
    el.x      = 10;
    const offset = this.template.elements.length * 3;
    el.y      = Math.min(10 + offset, this.template.heightMm - el.height - 5);
    el.zIndex = this.template.elements.length + 1;
    this.template.elements = [...this.template.elements, el];
    this.selectedElements = [el];
    this.pushHistory();
  }

  /** Called when the user commits a text edit (blur/Escape) on a free-text element. */
  onElementContentChanged(element: TemplateElement): void {
    this.updateElement(element);
    if (this.selectedElements[0]?.id === element.id) {
      this.selectedElements = [{ ...element }];
    }
    this.pushHistory();
  }

  /** Called when user clicks a tag in the panel (click-to-add). Places element with staggered offset. */
  onTagAddedFromPanel(tag: MergeTag): void {
    const el = createDefaultElement(tag.key, tag.label, tag.elementType);
    this.fitToCanvas(el);
    const offset = this.template.elements.length * 3;
    el.x = Math.min(10 + offset, this.template.widthMm  - el.width  - 5);
    el.y = Math.min(10 + offset, this.template.heightMm - el.height - 5);
    el.zIndex = this.template.elements.length + 1;
    this.template.elements = [...this.template.elements, el];
    this.selectedElements = [el];
    this.pushHistory();
  }

  /** Called when user drags a tag from the panel and drops it onto the canvas. Places at exact drop position. */
  onTagDroppedOnCanvas(event: TagDropEvent): void {
    const el = createDefaultElement(event.tag.key, event.tag.label, event.tag.elementType);
    this.fitToCanvas(el);
    // Clamp the drop position so the element stays fully within the canvas.
    el.x = Math.max(0, Math.min(event.x, this.template.widthMm  - el.width));
    el.y = Math.max(0, Math.min(event.y, this.template.heightMm - el.height));
    el.zIndex = this.template.elements.length + 1;
    this.template.elements = [...this.template.elements, el];
    this.selectedElements = [el];
    this.pushHistory();
  }

  /** Real-time update during drag/resize — no history push (avoids history spam on every mousemove). */
  onElementMoved(element: TemplateElement): void {
    this.updateElement(element);
  }

  /** Called once when drag/resize gesture ends (mouseup) — updates element and pushes history. */
  onElementMoveEnded(element: TemplateElement): void {
    this.updateElement(element);
    this.pushHistory();
  }

  onElementSelected(event: { element: TemplateElement | null; shiftKey: boolean }): void {
    if (!event.element) {
      this.selectedElements = [];
    } else if (event.shiftKey) {
      // Toggle membership in the selection.
      const idx = this.selectedElements.findIndex(e => e.id === event.element!.id);
      this.selectedElements = idx >= 0
        ? this.selectedElements.filter(e => e.id !== event.element!.id)
        : [...this.selectedElements, event.element!];
    } else {
      this.selectedElements = [event.element];
    }
  }

  /** Called when the rubber-band gesture finishes and encloses elements. */
  onMultiSelected(elements: TemplateElement[]): void {
    this.selectedElements = elements;
  }

  /** Real-time update during a group drag (no history push). */
  onGroupMoved(elements: TemplateElement[]): void {
    elements.forEach(el => this.updateElement(el));
  }

  /** Called once when the group drag mouseup fires — updates positions and pushes history. */
  onGroupMoveEnded(elements: TemplateElement[]): void {
    elements.forEach(el => this.updateElement(el));
    // Keep selectedElements in sync with the new positions.
    this.selectedElements = this.selectedElements.map(sel => {
      const updated = elements.find(e => e.id === sel.id);
      return updated ?? sel;
    });
    this.pushHistory();
  }

  onElementChanged(updated: TemplateElement): void {
    this.updateElement(updated);
    if (this.selectedElements[0]?.id === updated.id) {
      this.selectedElements = [{ ...updated }];
    }
    this.pushHistory();
  }

  removeElement(element: TemplateElement): void {
    this.template.elements = this.template.elements.filter(e => e.id !== element.id);
    this.selectedElements  = this.selectedElements.filter(e => e.id !== element.id);
    this.pushHistory();
  }

  /** Deletes all currently selected elements in one operation. */
  removeSelectedElements(): void {
    const ids = new Set(this.selectedElements.map(e => e.id));
    this.template.elements = this.template.elements.filter(e => !ids.has(e.id));
    this.selectedElements  = [];
    this.pushHistory();
  }

  private updateElement(updated: TemplateElement): void {
    const idx = this.template.elements.findIndex(e => e.id === updated.id);
    if (idx >= 0) {
      const elements = [...this.template.elements];
      elements[idx] = { ...updated };
      this.template.elements = elements;
    }
  }

  bringForward(element: TemplateElement): void {
    const updated = { ...element, zIndex: element.zIndex + 1 };
    this.onElementChanged(updated);
  }

  sendBackward(element: TemplateElement): void {
    const updated = { ...element, zIndex: Math.max(1, element.zIndex - 1) };
    this.onElementChanged(updated);
  }

  // ─── Undo / Redo ─────────────────────────────────────────────────────────
  public pushHistory(): void {
    if (this.isUndoRedo) return;
    this.history = this.history.slice(0, this.historyIndex + 1);
    this.history.push(JSON.parse(JSON.stringify(this.template.elements)));
    this.historyIndex = this.history.length - 1;
  }

  undo(): void {
    if (!this.canUndo) return;
    this.isUndoRedo = true;
    this.historyIndex--;
    this.template.elements = JSON.parse(JSON.stringify(this.history[this.historyIndex]));
    this.selectedElements = [];
    this.isUndoRedo = false;
  }

  redo(): void {
    if (!this.canRedo) return;
    this.isUndoRedo = true;
    this.historyIndex++;
    this.template.elements = JSON.parse(JSON.stringify(this.history[this.historyIndex]));
    this.selectedElements = [];
    this.isUndoRedo = false;
  }

  // ─── Save ─────────────────────────────────────────────────────────────────
  save(): void {
    if (!this.canUndo) return
    if (!this.template.name?.trim()) {
      this.messageService.add({ severity: 'warn', summary: this.translate.instant('bikeshop.receipt_template.editor.toast_required_summary'), detail: this.translate.instant('bikeshop.receipt_template.editor.toast_required_detail') });
      return;
    }
    console.log('Saving template', this.template);
    this.saving = true;
    this.templateService.saveTemplate(this.template).subscribe(saved => {
      this.template = saved;
      this.saving = false;
      this.messageService.add({ severity: 'success', summary: this.translate.instant('bikeshop.receipt_template.editor.toast_saved_summary'), detail: this.translate.instant('bikeshop.receipt_template.editor.toast_saved_detail', { name: saved.name }) });
      if (!this.templateId) {
        this.router.navigate(['/store/receipt-templates/editor', saved.id], { replaceUrl: true });
        this.templateId = saved.id;
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/store/receipt-templates']);
  }

  /**
   * Called when the Active toggle changes in the editor toolbar.
   * Reads the new state from the event (not from template.isActive, which
   * hasn't been updated yet since we use one-way [ngModel] binding).
   * For saved templates the service is called immediately so mutual
   * exclusivity is enforced without requiring a Save.
   * For unsaved templates the flag is stored on the model and saveTemplate()
   * enforces exclusivity when the user saves.
   */
  onActiveToggleChange(event: { checked: boolean }): void {
    this.template = { ...this.template, isActive: event.checked };
    if (this.templateId) {
      if (event.checked) {
        this.templateService.setActive(this.templateId, this.template.type).subscribe();
      } else {
        this.templateService.saveTemplate({ ...this.template, isActive: false }).subscribe();
      }
    }
  }

  // ─── Test-print job data ──────────────────────────────────────────────────

  /** Fetches a real job card by ID and maps it to canvas preview data. */
  loadTestJobData(): void {
    const raw = this.testJobCardId?.trim();
    if (!raw) return;
    const jobCardIdNum = Number(raw);
    if (isNaN(jobCardIdNum) || jobCardIdNum <= 0) {
      this.messageService.add({ severity: 'warn', summary: this.translate.instant('bikeshop.receipt_template.editor.toast_invalid_id_summary'), detail: this.translate.instant('bikeshop.receipt_template.editor.toast_invalid_id_detail') });
      return;
    }
    this.isLoadingTestData = true;
    this.liveJobData = null;

    this.serviceRequestService
      .getManageJobCardDetails(jobCardIdNum)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (jobcard: any) => {
     
            this.jobcardService
              .getJobCardQRIdentifier(jobcard.id)
              .subscribe((result) => {
                var url =
                  window.location.origin +
                  '/cyclist/jobcardAttachments/' +
                  jobcard.id +
                  '?QRCodeIdentifier=' +
                  result.qrCodeIdentifier +
                  '&Theme=' +
                  jobcard.partnerID;

                jobcard.qrCodeURL = url;

                this.checklistService
                  .getServiceChecklist(
                    jobcard.partnerID,
                    jobcard.id,
                    this.helpersService.getPartnerData().Print_ShowOnlyPreServiceCheck,
                  )
                  .subscribe(
                    (checklist) => {
                      var checklists = checklist;
                     //   .filter((x) => {
                     //   return x.mandatoryForAllServices || x.linkedToServiceTypes;
                     // });

                      // Fetch attachments and inject image URLs into the live data map.
                      const serviceId = jobcard?.id ?? jobcard?.serviceID ?? jobCardIdNum;
                      this.jobcardService.getJobCardAttachments(serviceId)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe((attachments: any[]) => {
                          const imageUrls = (attachments ?? [])
                            .filter((a: any) => (a.mediaType ?? '').indexOf('image') >= 0)
                            .map((a: any) => a.mediaURL as string)
                            .filter(Boolean);

                          jobcard.checklists = checklists;
                          const data = mapJobCardToSampleData(jobcard, this.appConfig.getStatusList(), this.helpersService.getPartnerData(), this.translate);
                          // Merge image URLs into the mapped data in one assignment so they
                          // are never overwritten by a subsequent this.liveJobData = data call.
                          this.liveJobData = imageUrls.length > 0
                            ? { ...data, images: imageUrls }
                            : data;
                          this.isLoadingTestData = false;
                          this.messageService.add({
                            severity: 'success',
                            summary: this.translate.instant('bikeshop.receipt_template.editor.toast_test_data_summary'),
                            detail: this.translate.instant('bikeshop.receipt_template.editor.toast_test_data_detail', { jobCardNo: jobcard.jobCardNo ?? raw }),
                          });
                        });

                      
                    },
                    (err) => {
                    },
                  );

               
          
          });

         
        },
        (_err: any) => {
          this.isLoadingTestData = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('bikeshop.receipt_template.editor.toast_not_found_summary'),
            detail: this.translate.instant('bikeshop.receipt_template.editor.toast_not_found_detail', { id: raw }),
          });
        },
      );
  }

  /** Clears the loaded job card data, reverting to sample / placeholder mode. */
  clearTestJobData(): void {
    this.liveJobData   = null;
    this.testJobCardId = '';
  }

  // ─── Print ────────────────────────────────────────────────────────────────
  testPrint(): void {
    // Snap to 100 % zoom so the canvas renders at its natural mm dimensions.
    const prevZoom = this.zoom;
    this.zoom = 100;

    // Wait one tick for Angular to apply the zoom change to the DOM.
    setTimeout(() => {
      const canvasEl = document.querySelector('.tc-canvas') as HTMLElement | null;
      if (!canvasEl) {
        this.zoom = prevZoom;
        this.messageService.add({ severity: 'warn', summary: this.translate.instant('bikeshop.receipt_template.editor.toast_print_warn_summary'), detail: this.translate.instant('bikeshop.receipt_template.editor.toast_print_canvas_not_found') });
        return;
      }

      // Force a synchronous measurement pass so auto-height elements are
      // correctly sized before capture.  The print window will redo layout
      // from scratch using the data-* attributes after its own images load.
      if (this.canvasRef) {
        this.canvasRef.forceRemeasure();
      }

      const opened = this.printService.print(canvasEl, this.template.name);
      if (!opened) {
        this.messageService.add({ severity: 'warn', summary: this.translate.instant('bikeshop.receipt_template.editor.toast_print_warn_summary'), detail: this.translate.instant('bikeshop.receipt_template.editor.toast_print_popup_blocked') });
      }

      this.zoom = prevZoom;
    }, 60);
  }
}
